import { ApiError } from "./ApiError";
import { authStore } from "@/features/auth/store/authStore";
import { AuthUnavailableError } from "@/features/auth/authErrors";
import { getCookie } from "@/lib/functions/cookies";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
export const EVOGPS_WS_BASE_URL = (import.meta.env.VITE_API_EVOGPS_WS as string | undefined) ?? "";

const CSRF_COOKIE_NAME = "csrf_token";
const CSRF_HEADER_NAME = "X-CSRF-Token";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiFetchConfig extends Omit<RequestInit, "method" | "body"> {
  method: HttpMethod;
  path: string;
  /** Base URL to call; defaults to VITE_API_BASE_URL. A custom value is treated as an unauthenticated API: no cookies, CSRF header or 401 refresh. */
  baseUrl?: string;
  queryParams?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  /** Defaults to "json" (JSON/text auto-parse); use "blob" for file downloads. */
  responseType?: "json" | "blob";
  /** Skip the X-CSRF-Token header (only for the backend's own excluded /api/auth/login + /api/auth/refresh). */
  skipCsrf?: boolean;
  /** Skip the silent-refresh-then-retry-once on 401 (used by login/refresh/logout themselves). */
  skipAuthRetry?: boolean;
}

const buildUrl = (
  baseUrl: string,
  path: string,
  queryParams?: ApiFetchConfig["queryParams"],
): string => {
  if (!queryParams) return `${baseUrl}${path}`;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(queryParams)) {
    if (value !== undefined) search.append(key, String(value));
  }
  const queryString = search.toString();
  return queryString ? `${baseUrl}${path}?${queryString}` : `${baseUrl}${path}`;
};

const buildHeaders = (config: ApiFetchConfig): Headers => {
  const headers = new Headers(config.headers);
  // For FormData the browser must set Content-Type itself (multipart boundary).
  if (config.body !== undefined && !(config.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const isDefaultBaseUrl = config.baseUrl === undefined || config.baseUrl === API_BASE_URL;
  if (config.method !== "GET" && !config.skipCsrf && isDefaultBaseUrl) {
    const csrfToken = getCookie(CSRF_COOKIE_NAME);
    if (csrfToken) headers.set(CSRF_HEADER_NAME, csrfToken);
  }
  return headers;
};

const parseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

/**
 * Generic JSON fetch wrapper for the Catasto API.
 * - Always sends cookies (`credentials: "include"`) so the httpOnly access/refresh-token cookies flow.
 * - Attaches the CSRF cookie value as a header on mutating requests unless `skipCsrf` is set.
 * - On a 401, attempts a single silent refresh and retries the request once, unless `skipAuthRetry` is set.
 */
export const apiFetch = async <T>(config: ApiFetchConfig): Promise<T> => {
  const {
    path,
    baseUrl = API_BASE_URL,
    queryParams,
    body,
    skipAuthRetry,
    responseType,
    ...requestInit
  } = config;
  const isDefaultBaseUrl = baseUrl === API_BASE_URL;

  const doFetch = (): Promise<Response> =>
    fetch(buildUrl(baseUrl, path, queryParams), {
      ...requestInit,
      credentials: isDefaultBaseUrl ? "include" : "omit",
      headers: buildHeaders(config),
      body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
    });

  let response = await doFetch();

  if (response.status === 401 && !skipAuthRetry && isDefaultBaseUrl) {
    const refreshResult = await authStore.silentRefresh();
    if (refreshResult.status === "authenticated") {
      response = await doFetch();
    } else if (refreshResult.status === "unavailable") {
      throw new AuthUnavailableError();
    }
  }

  if (!response.ok) {
    const details = await parseBody(response);
    throw new ApiError(
      `Request to ${path} failed with status ${response.status}`,
      response.status,
      details,
    );
  }

  if (responseType === "blob") return (await response.blob()) as T;

  return (await parseBody(response)) as T;
};
